/**
 * useFriendRequests Hook
 * Fetches paginated incoming and outgoing friend requests.
 * Shared between web and mobile.
 */

import { useCallback, useState } from 'react';
import type { HttpClient } from '../api/httpClient';
import type {
  IncomingFriendRequest,
  OutgoingFriendRequest,
  PaginatedIncomingRequests,
  PaginatedOutgoingRequests,
} from '../friendTypes';

// =============================================================================
// Types
// =============================================================================

export interface UseFriendRequestsOptions {
  httpClient: HttpClient;
  isAuthenticated: boolean;
  pageSize?: number;
}

export interface UseFriendRequestsResult {
  incoming: IncomingFriendRequest[];
  outgoing: OutgoingFriendRequest[];
  incomingTotal: number;
  outgoingTotal: number;
  incomingHasMore: boolean;
  outgoingHasMore: boolean;
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
  loadMoreIncoming: () => Promise<void>;
  loadMoreOutgoing: () => Promise<void>;
}

// =============================================================================
// Hook
// =============================================================================

export function useFriendRequests(options: UseFriendRequestsOptions): UseFriendRequestsResult {
  const { httpClient, isAuthenticated, pageSize = 20 } = options;

  const [incoming, setIncoming] = useState<IncomingFriendRequest[]>([]);
  const [outgoing, setOutgoing] = useState<OutgoingFriendRequest[]>([]);
  const [incomingTotal, setIncomingTotal] = useState(0);
  const [outgoingTotal, setOutgoingTotal] = useState(0);
  const [incomingHasMore, setIncomingHasMore] = useState(false);
  const [outgoingHasMore, setOutgoingHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!isAuthenticated) return;

    setLoading(true);
    setError(null);

    try {
      const [incomingData, outgoingData] = await Promise.all([
        httpClient.get<PaginatedIncomingRequests>('/api/friends/requests/incoming', {
          limit: pageSize,
          offset: 0,
        }),
        httpClient.get<PaginatedOutgoingRequests>('/api/friends/requests/outgoing', {
          limit: pageSize,
          offset: 0,
        }),
      ]);
      setIncoming(incomingData.requests);
      setIncomingTotal(incomingData.total);
      setIncomingHasMore(incomingData.hasMore);
      setOutgoing(outgoingData.requests);
      setOutgoingTotal(outgoingData.total);
      setOutgoingHasMore(outgoingData.hasMore);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load friend requests');
    } finally {
      setLoading(false);
    }
  }, [httpClient, isAuthenticated, pageSize]);

  const loadMoreIncoming = useCallback(async () => {
    if (!isAuthenticated || !incomingHasMore || loading) return;

    setLoading(true);
    setError(null);

    try {
      const data = await httpClient.get<PaginatedIncomingRequests>(
        '/api/friends/requests/incoming',
        { limit: pageSize, offset: incoming.length },
      );
      setIncoming((prev) => [...prev, ...data.requests]);
      setIncomingHasMore(data.hasMore);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load more incoming requests');
    } finally {
      setLoading(false);
    }
  }, [httpClient, isAuthenticated, incomingHasMore, loading, incoming.length, pageSize]);

  const loadMoreOutgoing = useCallback(async () => {
    if (!isAuthenticated || !outgoingHasMore || loading) return;

    setLoading(true);
    setError(null);

    try {
      const data = await httpClient.get<PaginatedOutgoingRequests>(
        '/api/friends/requests/outgoing',
        { limit: pageSize, offset: outgoing.length },
      );
      setOutgoing((prev) => [...prev, ...data.requests]);
      setOutgoingHasMore(data.hasMore);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load more outgoing requests');
    } finally {
      setLoading(false);
    }
  }, [httpClient, isAuthenticated, outgoingHasMore, loading, outgoing.length, pageSize]);

  return {
    incoming,
    outgoing,
    incomingTotal,
    outgoingTotal,
    incomingHasMore,
    outgoingHasMore,
    loading,
    error,
    reload,
    loadMoreIncoming,
    loadMoreOutgoing,
  };
}
